<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>

        <div
            v-if="initialLoading"
            key="initialLoading"
            class="animate__animated animate__fadeIn animate_fadeOut animate__fast bg-gray-50 rounded-lg"
        >
            <!-- <busy-spinner inner-class="my-5" text="Loading Sourcing Events..."></busy-spinner> -->
            <b-skeleton-table :rows="skeletonRows" :columns="fieldsToShow.length" show-footer :table-props="{bordered: true}"></b-skeleton-table>
        </div>

        <div 
            v-if="!initialLoading"
            key="initialLoadingDone"
        >
            <div 
                v-if="hasAnyFilters"
                class="d-flex flex-wrap justify-content-between align-items-end mb-3 animate__animated animate__fast animate__fadeInUp"
            >  <!-- Filter Area -->
                <base-search-box
                    v-if="isFilterEnabled('search')"
                    :value="currentFilterValues.searchFilter"
                    ref="searchBox"
                    placeholder="Quick Search..."
                    @search="setFilterAndSearch('searchFilter', $event)"
                    @reset="setFilterAndSearch('searchFilter', '')"
                ></base-search-box>
                <b-form inline class="mt-2 ml-auto">
                    <div class="align-self-end d-flex gap-2">
                        <b-button 
                            variant="soft-primary" 
                            :disabled="!showFilterReset" 
                            class="text-uppercase" 
                            @click="clearAllFilters"
                        >Clear Filters
                        </b-button>
                    </div>
                    <base-filter-select
                        label="My Role"
                        v-if="isFilterEnabled('owner')"
                        v-model="currentFilterValues.grouping"
                        :reset-value="initialFilterValues.grouping"
                        :options="groupingOptions"
                        @change="getNewList"
                    ></base-filter-select>
                    <base-filter-select
                        label="Owner"
                        v-if="isFilterEnabled('sourcing_owner')"
                        v-model="currentFilterValues.ownerFilter"
                        :reset-value="initialFilterValues.ownerFilter"
                        first-option-text="All Owners"
                        :options="setupData.sourcing_owner_options"
                        @change="getNewList"
                    ></base-filter-select>
                    <base-filter-select
                        label="Event Type"
                        v-if="isFilterEnabled('rfx_type')"
                        v-model="currentFilterValues.rfxTypeFilter"
                        :reset-value="initialFilterValues.rfxTypeFilter"
                        first-option-text="All Types"
                        :options="setupData.rfx_type_options"
                        @change="getNewList"
                    ></base-filter-select>
                    <base-filter-select
                        label="State"
                        v-if="isFilterEnabled('sm_state')"
                        v-model="currentFilterValues.stateFilter"
                        :reset-value="initialFilterValues.stateFilter"
                        :options="setupData.state_options"
                        value-field="value"
                        text-field="title"
                        @change="getNewList"
                    ></base-filter-select>  <!-- first-option-text="Any Status" only used when setting blank first option -->
                    <base-filter-select
                        label="Award Status"
                        v-if="isFilterEnabled('award_status')"
                        v-model="currentFilterValues.awardStatusFilter"
                        :reset-value="initialFilterValues.awardStatusFilter"
                        :options="awardStatusOptions"
                        @change="getNewList"
                    ></base-filter-select>
                    <base-filter-select
                        label="Category"
                        v-if="isFilterEnabled('sourcing_category')"
                        v-model="currentFilterValues.categoryFilter"
                        :reset-value="initialFilterValues.categoryFilter"
                        first-option-text="All Categories"
                        :options="setupData.sourcing_category_options"
                        @change="getNewList"
                    ></base-filter-select>
                    <base-filter-select
                        label="Tags"
                        v-if="isFilterEnabled('sourcing_tags')"
                        v-model="currentFilterValues.tagsFilter"
                        :reset-value="initialFilterValues.tagsFilter"
                        first-option-text="All Tags"
                        :options="setupData.sourcing_tag_options"
                        @change="getNewList"
                    ></base-filter-select>
                    <b-button
                        v-b-tooltip.hover title="Refresh List"
                        variant="light"
                        class="ml-2 align-self-end"
                        @click="refreshList(true)"
                    >
                        <b-icon icon="arrow-repeat"></b-icon>
                    </b-button>
                </b-form>
                <!-- <div v-if="!loading && totalRows" class="font-size-90 text-muted">{{totalRows}} Sourcing Events</div> -->
            </div>

            <b-card 
                id="rfx-table-card"
                class="shadow rounded-lg animate__animated animate__fadeIn animate__fast"
                no-body
            >
                <transition name="fade-medium" mode="out-in">

                    <div
                        v-if="!loading && !totalRows"
                        key="emptyList"
                        class="d-flex justify-content-center align-items-center"
                        style="min-height: 200px;"
                    >
                        <div class="text-center">
                            <div class="font-size-100 text-muted mb-4">
                                {{ showFilterReset ? 'No sourcing events found.  Try a different search' : 'No sourcing events.' }}
                            </div>
                            <b-button v-if="showFilterReset" variant="soft-primary" @click="clearAllFilters">
                                Clear Search
                            </b-button>
                            <b-button v-if="!showFilterReset && isInternal && $currentUserInfo.is_paid_seat" size="lg" variant="soft-success" @click="handleAddNewRfx">
                                Create New RFx
                            </b-button>
                        </div>
                    </div>

                    <div
                        v-else
                        key="notEmptyList"
                    >

                        <!-- <div v-if="isList">

                            <div v-for="item in itemList" :key="item.id">
                                {{ item.title }}
                            </div>

                        </div> -->

                        <b-table 
                            :items="itemList"
                            :fields="fieldsToShow"
                            :busy="loading"
                            primary-key="id" 
                            responsive
                            no-local-sorting
                            no-sort-reset
                            :sort-by.sync="sortByField"
                            :sort-desc.sync="sortDesc"
                            @sort-changed="getNewList"
                            hover
                            :small="small"
                            tbody-class="cells-align-middle"
                            class="no-top-border td-align-middle mb-0 rfx-list-base-table"
                            thead-class="overflow-hidden cells-align-middle"
                            head-variant=""
                        >
                            <!-- <template v-slot:table-busy>
                                <div class="text-center text-primary my-2">
                                    <b-spinner class="align-middle"></b-spinner>
                                    <strong>Loading...</strong>
                                </div>
                            </template> -->

                            <!-- ROW DETAILS -->  <!-- Hello from the details of RFx {{row.item.id}} -->
                            <template #row-details="row">
                                <rfx-manage-responses-new :rfx-pk="row.item.id"></rfx-manage-responses-new>
                            </template>

                            <!-- COLUMNS -->
                            <template #cell(detailsToggle)="data">
                                <b-button
                                    size="md"
                                    variant="minimal-secondary"
                                    @click="data.toggleDetails"
                                >
                                    <b-icon :icon="data.detailsShowing ? 'dash-circle' : 'plus-circle'"></b-icon>
                                </b-button>
                            </template>
                            <template v-slot:cell(title)="data">
                                <b-link :href="data.item[linkField]">{{ data.value }}
                                    <new-badge 
                                        v-if="data.item.has_any_unread_messages"
                                        small 
                                        x-margin="1"
                                    ></new-badge>
                                </b-link>
                            </template>
                            <template #cell(sm_state)="data">
                                <status-object-badge
                                    :status-object="data.item.state_dict" 
                                    text-uppercase
                                    font-size="0.8rem"
                                    title-field="badge_title"
                                    no-wrap
                                ></status-object-badge>
                            </template>
                            <template #cell(invited_count)="data">
                                <b-link :href="data.item.manage_invites_url">
                                    {{ data.value === 0 ? '-' : data.value }}
                                </b-link>
                            </template>
                            <template #cell(submitted_count)="data">
                                <b-link v-if="data.value > 0" :href="data.item.internal_manage_url">
                                    {{ data.value }}
                                </b-link>
                                <span v-else class="text-muted font-weight-normal">&ndash;</span>
                            </template>
                            <template #cell(sourcing_category_name)="data">
                                <div class="small">
                                    <div class="">{{data.value}}</div>
                                    <div class="text-gray-400 font-weight-700">{{data.item.rfx_type}}</div>
                                </div>
                            </template>
                            <!-- <template #cell(latest_messages)="data">
                                <div class="font-size-70 border rounded overflow-hidden">
                                    <div class="d-flex gap-2 px-1 bg-danger-50">
                                        <div class="font-weight-bold">E</div>
                                        <div class="text-center">{{ formatDate(data.item.latest_external_message_date) }}</div>
                                    </div>
                                    <div class="d-flex gap-2 px-1 bg-primary-50">
                                        <div class="font-weight-bold">I</div>
                                        <div class="text-center">{{ formatDate(data.item.latest_internal_message_date) }}</div>
                                    </div>
                                </div>
                            </template> -->
                            <template #cell(vendor_count)="data">
                                <div class="d-flex justify-content-start gap-2 align-items-baseline">
                                    <b-link v-if="data.item.invited_count > 0" :href="data.item.internal_manage_url" class="d-block" style="min-width: 1.5rem;">
                                        {{ data.item.invited_count }}
                                    </b-link>
                                    <div v-else class="text-muted font-weight-normal" style="min-width: 1.5rem;">&ndash;</div>
                                    <span class="font-size-70 font-weight-400 text-uppercase text-muted">Invited</span>
                                </div>
                                <div class="d-flex justify-content-start gap-2 align-items-baseline">
                                    <b-link v-if="data.item.submitted_count > 0" :href="data.item.internal_manage_url" class="d-block" style="min-width: 1.5rem;">
                                        {{ data.item.submitted_count }}
                                    </b-link>
                                    <div v-else class="text-muted font-weight-normal" style="min-width: 1.5rem;">&ndash;</div>
                                    <span class="font-size-70 font-weight-400 text-uppercase text-muted">Submitted</span>
                                </div>
                            </template>
                            <template #cell(awarded_responses)="data">
                                <!-- cell is awarded_count, but we SHOW awarded_responses (array of response objects) -->
                                <div v-if="data.value && Array.isArray(data.value) && data.value.length > 0" class="">
                                    <b-link 
                                        v-for="resp in data.value" 
                                        :key="resp.id" 
                                        :href="resp.manage_response_url"
                                        class="d-block"
                                    >{{ resp.vendor_name }}</b-link>
                                </div>
                                <div v-else class="text-muted text-uppercase">&mdash;</div>
                            </template>
                            <template #cell(created)="data">
                                {{ data.value | dateFilter }}
                            </template>
                            <template #cell(modified)="data">
                                {{ data.value | dateFilter }}
                            </template>
                            <template #cell(latest_submission_date)="data">
                                {{ humanizeDateTime(data.value) || '-' }}
                            </template>
                            <template #cell(latest_external_message_date)="data">
                                <span v-b-tooltip.hover title="Unread message" class="font-size-80" :class="data.item.has_unread_external_messages ? 'font-weight-bold' : ''">
                                    <b-icon 
                                        v-if="data.item.has_unread_external_messages" 
                                        icon="circle-fill" 
                                        variant="danger" 
                                        class="bg-danger-100 rounded-circle" 
                                        style="padding: 3px;"
                                    ></b-icon>
                                    {{ humanizeDateTime(data.value) || '-' }}
                                </span>
                            </template>
                            <template #cell(latest_internal_message_date)="data">
                                <span v-b-tooltip.hover title="Unread message" class="font-size-80" :class="data.item.has_unread_internal_messages ? 'font-weight-bold' : ''">
                                    <b-icon 
                                        v-if="data.item.has_unread_internal_messages" 
                                        icon="circle-fill" 
                                        variant="danger" 
                                        class="bg-danger-100 rounded-circle" 
                                        style="padding: 3px;"
                                    ></b-icon>
                                    {{ humanizeDateTime(data.value) || '-' }}
                                </span>
                            </template>
                            <template #cell(close_date)="data">
                                <date-time-deadline :date="data.value" empty-text="Not Set"></date-time-deadline>
                            </template>
                            <template #cell(actions)="data">
                                <b-dropdown variant="gray-secondary" no-caret size="sm" toggle-class="border shadow-sm">
                                    <template #button-content>
                                        <b-icon icon="three-dots"></b-icon>
                                    </template>
                                    <b-dropdown-item :href="data.item.internal_url">
                                        <b-icon icon="lightning" variant="warning" class="mr-2"></b-icon>
                                        Summary
                                    </b-dropdown-item>
                                    <b-dropdown-item v-if="$currentUserInfo.is_paid_seat" :href="data.item.manage_invites_url">
                                        <b-icon icon="building" variant="primary" class="mr-2"></b-icon>
                                        Invitations
                                    </b-dropdown-item>
                                    <b-dropdown-item :href="data.item.internal_manage_url">
                                        <b-icon icon="card-list" variant="info" class="mr-2"></b-icon>
                                        Responses
                                    </b-dropdown-item>
                                </b-dropdown>
                            </template>
                        </b-table>
                    </div>
                
                </transition>

                <template #footer v-if="!loading && totalRows">
                    <base-table-pagination
                        :current-page.sync="currentPage"
                        :per-page.sync="perPage"
                        :total-items="totalRows"
                        :show-per-page="showPerPage"
                        item-name="Sourcing Events"
                        @update:perPage="getNewList"
                        @update:currentPage="getList"
                    ></base-table-pagination>
                </template>
            </b-card>

            <div class="pt-3 d-flex gap-2"> <!-- below grid -->
                <b-button 
                    v-if="saveState"
                    variant="soft-primary" 
                    :disabled="!hasSavedState" 
                    class="" 
                    @click="clearSavedStateAndRefresh"
                >Reset to List Defaults
                </b-button>
            </div>

        </div>

    </div>
</template>

<script>
import { suppliosApi } from '../js/api-service';
import BaseTablePagination from './BaseTablePagination';
import { FormatDateHelper, FormatDateTimeLocal, HumanizeDateTimeHelper, DateApproachingDeadline } from '../js/filters-helpers';
import DateTimeDeadline from './table-cell-comps/DateTimeDeadline';
import BaseSearchBox from './BaseSearchBox';
import BaseFilterSelect from './BaseFilterSelect';
import StatusObjectBadge from './functional-comps/StatusObjectBadge';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import { currentUserInfoMixin, splTableDefaults } from '../js/custom-vue-mixins';
import { Rfx } from '../js/models/rfx';

export default {
    name: 'RfxListBase',
    components: {
        BaseTablePagination,
        BaseSearchBox,
        BaseFilterSelect,
        StatusObjectBadge,
        DateTimeDeadline,
    },
    mixins: [currentUserInfoMixin],
    props: {
        isList: {  // if true, renders a list, not table -- NOT IMPLEMENTED YET
            type: Boolean,
            default: false,
        },
        showFields: {  // aray of strings of fields to show, based on 'key' values in internal field defs
            type: Array,
            required: true,
        },
        showFilters: {  // array of strings that correspond to which filters to show
            // pass empty list to hide all filters -- leave off (no prop) to show all filters
            type: Array,
            default: () => {
                return ['search', 'owner', 'rfx_type', 'sm_state', 'sourcing_category'];
            }
        },
        presetFilters: { // pass in an object in the same format as defaultFilterValues with any filters you want to pre-set
            // if you also hide that same filter, then it will be 'hard-coded' and not changeable by user
            // this lets you use an existing endpoint, but with hard-coded filter values, like {grouping: 'owned'}
            // how it works:  we use Object.assign() to merge this with defaultFilterValues on created --> set that as a new object on initialFilterValues
            type: Object,
            required: false,
        },
        endpointName: {  // api endpoint on suppliosApi.rfx to get - options are 'list', 'getPortalListings' or 'getPublicListings' or 'getInvitedListings'
            type: String,
            default: 'list',
        },
        linkField: {  // field to use in link to detail view -- should be 'internal_url' or 'vendor_url'
            type: String,
            default: 'internal_url',
        },
        initialSortField: {  // initial field for sortBy, does not have to be in showFields array, but must be in fields array and must be in the ordering options for API endpoint
            type: String,
            default: 'created',
        },
        initialSortDesc: {  // initial sort in descending order?
            type: Boolean,
            default: false,
        },
        isInternal: {  // set true for using in internal tenant use cases -- turns things on/off like add new RFX button in empty list, etc.
            type: Boolean,
            default: false,
        },
        fixedPerPage: {  // if you want to force a fixed # per page AND HIDE PER-PAGE SELECTOR
            // this is useful for dashboard views where you want to control size of grid
            // validator... we use same one in base-table-pagination component, so using here too
            type: [String, Number],
            required: false,
            validator: function (value) {
                return splTableDefaults.perPageOptions.includes(value);
                //return defaultPerPageOptions.includes(value);
            },
        },
        small: {
            type: Boolean,
            default: false,
        },
        saveState: {  // if true, will save state of grid in local storage
            type: Boolean,
            default: false,
        },
        enableDetails: {  // if true, will enable the 'show details' for each row - with response info, etc.
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            initialLoading: true,  // set to false after getting list the first time
            loading: false,  // loading signal for fetching list
            currentPage: 1,  // will get sync'd by base-table-pagination
            perPage: 25,  // will get sync'd by base-table-pagination, and also set by our mixin default value during create hook
            showPerPage: true,  // if we set fixedPerPage prop, this is set false in created() - and passed to same prop in pagination component
            totalRows: null,  // total items in list... set from response.data.count
            itemList: [],   // list from API
            fields: [
                // for column widths, do not set fixed or responsive, then use class: on field def object with either min-width or max-width (but not width directly)
                {
                    key: 'detailsToggle',
                    label: '',
                    sortable: false,
                    class: 'max-width-50 text-center',
                },{
                    key: 'title',
                    label: 'Event Title',
                    sortable: true,
                    api_ordering_name: 'title',  // set this for any sortable field, this is what gets sent to API for ordering=_____
                    tdClass: 'font-weight-bold'
                },/* {
                    key: 'rfx_type',  // combined with category to fit more in
                    label: 'Event Type',
                    sortable: true,
                    api_ordering_name: 'rfx_type',
                    class: 'max-width-80',
                }, */{
                    key: 'sm_state',
                    label: 'State',
                    sortable: true,
                    api_ordering_name: 'sm_state',
                    class: 'text-center max-width-120'
                },{
                    key: 'sourcing_category_name',
                    label: 'Category',
                    sortable: true,
                    api_ordering_name: 'sourcing_category__name',
                    class: 'text-center max-width-120',
                    tdClass: 'small',
                },{
                    key: 'invited_count',
                    label: 'Invited Vendors',
                    sortable: true,
                    api_ordering_name: 'invited_count',
                    class: 'text-center max-width-100',
                    tdClass: 'font-weight-bold',
                },{
                    key: 'submitted_count',
                    label: 'Submitted Responses',
                    sortable: true,
                    api_ordering_name: 'submitted_count',
                    class: "text-center max-width-120",
                    tdClass: 'font-weight-bold',
                },/* {
                    key: 'latest_messages',
                    label: 'Latest Msg',
                    sortable: true,
                    // TODO - need to combined internal/external threads: api_ordering_name: 'has_unread_external_messages',
                    class: "text-center max-width-100",
                }, */{
                    key: 'vendor_count',  // COMBINED invited_count / submitted_count to save space
                    label: 'Vendors',
                    sortable: false,
                    api_ordering_name: 'submitted_count',  // not used
                    class: "text-center min-width-110 max-width-130",
                    tdClass: 'font-weight-500',
                },{
                    key: 'awarded_responses',
                    label: 'Awarded',
                    sortable: false,  // looks like it should order by vendor name, but not doing that.. so no sorting
                    api_ordering_name: 'awarded_count',  // if you DID order, you could order by this
                    class: "text-center max-width-120",
                    tdClass: '',
                },{
                    key: 'awarded_count',  // we get these, but showing awarded_responses is better
                    label: 'Awarded',
                    sortable: true,
                    api_ordering_name: 'awarded_count',
                    class: "text-center max-width-120",
                    tdClass: '',
                },{
                    key: 'sourcing_owner_name',
                    label: 'Sourcing Owner',
                    sortable: false,
                    class: 'text-center',
                    tdClass: '',
                },{
                    key: 'created',
                    label: 'Date Created',
                    sortable: true,
                    api_ordering_name: 'created',
                    class: 'text-center'
                },{
                    key: 'modified',
                    label: 'Last Modified',
                    sortable: true,
                    api_ordering_name: 'modified',
                    class: 'text-center'
                },{
                    key: 'latest_submission_date',
                    label: 'Latest Submission',
                    sortable: true,
                    api_ordering_name: 'latest_submission_date',
                    class: 'text-center'
                },{
                    key: 'latest_external_message_date',
                    label: 'Last Vendor Activity',
                    sortable: true,
                    api_ordering_name: 'latest_external_message_date',
                    class: 'text-center max-width-120'
                },{
                    key: 'latest_internal_message_date',
                    label: 'Last Internal Comment',
                    sortable: true,
                    api_ordering_name: 'latest_internal_message_date',
                    class: 'text-center max-width-120'
                },{
                    key: 'close_date',
                    label: 'Response Deadline',
                    sortable: false,  // just for indicating... not sortable because you have 2 different too confusing -- use Coelesce to make new DB annotation and sort by that
                    formatter: (value, key, item) => {
                        return item.close_date_to_show;
                    },
                    api_ordering_name: 'close_date',  // SHOULD BE close_date_planned?  close_date_to_show?
                    class: 'text-center'
                },{
                    key: 'close_date_planned',  // DON'T SHOW THIS - just for sorting
                    label: 'Response Deadline',
                    sortable: true,  // KEEP THIS true - but DO NOT SHOW THIS FIELD - need it to be sortable so we can sort by it for portal/public listing views, but does not have to be displayed to be used for sort
                    api_ordering_name: 'close_date_planned',  // make sure backend can handle this!
                    class: 'text-center'
                },{
                    key: 'actions',
                    label: 'Actions',
                    class: 'text-center',
                }
            ],
            // FILTERS & GROUPING
            loadingSetupData: false,  // flag for loading setup data
            setupData: {},  // options for various select boxes (rfx_type, sourcing_category, etc) from getSetupData() is called
            currentFilterValues: {},  // set this using Object.assign({}, this.initialFilterValues) during created hook
            initialFilterValues: {},  // we merge this.defaultFilterValues with any presetFilters (prop) in created hook to make this
            defaultFilterValues: {  // Object.assign({}, initialFilterValues) on create, and when you reset
                // don't change these during lifecycle... they are contants
                searchFilter: '',
                grouping: 'all',  // groupings by current TenantUser visibility - for main list view, pass in prop to use 'participant' IF flags is set to allow all internal TUs to view all RFX - March 15 2023
                rfxTypeFilter: '',  // filter by rfx_type (RFQ, RFP, etc.) - will be the string value
                categoryFilter: null,  // filter by sourcing_category - will be the ID of sourcing_category
                stateFilter: '__not_archived__',  // filter by sm_state
                ownerFilter: null,  // filter by sourcing_owner - ID of sourcing_owner TU - currently only single-select
                awardStatusFilter: null,  // options are null, 'awarded', 'unawarded'
                tagsFilter: null,  // filter by tags - will be the ID of the tag
            },
            groupingOptions: [  // make this computed based on currentUserInfo role?  
                {
                    value: 'all',  // for the main list view internally, we will NOT make this the default - use participant instead
                    text: 'All Events',
                },{
                    value: 'owned',
                    text: 'Owner'
                },{
                    value: 'admin',
                    text: 'Owner or Admin'
                },{
                    value: 'participant',
                    text: 'On The Team'  // was 'Any Role' until March 15 2023
                }
            ],
            awardStatusOptions: [  // make sure these match backend - later, get dynamically from backend
                {
                    value: null,
                    text: 'Any',
                },{
                    value: 'awarded',
                    text: 'Awarded'
                },{
                    value: 'unawarded',
                    text: 'Not Awarded'
                },
            ],
            hasAppliedFilters: false,  // set this each time we getList() based on whether we used filters or not
            // for sorting:
            sortByField: 'close_date',  // default gets set via prop... sortByField and sortDesc will get .sync'd by b-table, then gets used in API call query params
            sortDesc: true,  // sort descending = true (adds '-' sign in backend), sort ascending = false (no minus sign)
        }
    },
    computed: {
        showFilterReset: function() {
            // returns BOOLEAN if any currentFilterValues are different than initialFilterValues
            let isSame = isEqual(this.currentFilterValues, this.initialFilterValues);
            return !isSame;
        },
        fieldsToShow: function() {
            // determine our final list of fields to show based on this.showFields prop and this.fields complete list
            const combinedShowFields = [];
            if (this.enableDetails) {
                combinedShowFields.push('detailsToggle');
            }
            combinedShowFields.push(...this.showFields);
            return this.fields.filter(fld => combinedShowFields.includes(fld.key));
        },
        hasAnyFilters: function() {
            // determine if there are ANY filters specified to be shown based on showFilters prop
            // if so, return true, if no filters shown, return false
            // used to show/hide the filter area completely
            return this.showFilters.length > 0;
        },
        skeletonRows() {
            // the number of rows to show when loading
            // you might have perPage of 25, but likely only will show ~10... so make it closer to that
            // if you have fixedPerPage -- use that 
            return this.perPage;
        },
        localStorageKey() {
            // key to use for saving state of grid in local storage - perPage, columns, filters? 
            return `splKey_${window.location}_${this.$options.name}`;
        },
        hasSavedState() {
            // do we have a saved state in local storage? -- this is NOT reactive when localStorage changes
            return localStorage.getItem(this.localStorageKey) ? true : false;
        },
    },
    filters: {
        dateFilter: function(value) {
            return FormatDateHelper(value);
        },
        dateTimeFilter: function(value) {
            return FormatDateTimeLocal(value);
        },
        humanizeDateTime: function(value) {
            return HumanizeDateTimeHelper(value);
        },
    },
    created: function() {
        this.setInitialFromDefaults();
        /* // merge any presetFilters (object from prop) with this.defaultFilterValues to make this.initialFilterValues
        if (this.presetFilters && !isEmpty(this.presetFilters)) {
            // target is empty object so you don't mutate originals, merge presetFilters last
            this.initialFilterValues = Object.assign({}, this.defaultFilterValues, this.presetFilters);  
        } else {
            // if we DO NOT have any presetFilters, we need to just do same thing, but without presetFilters... maybe there's a shorter way, but this works
            this.initialFilterValues = Object.assign({}, this.defaultFilterValues);
        }
        this.resetFilterValues();
        // FIGURE OUT THE perPage to use
        if (this.fixedPerPage) {
            this.perPage = Number(this.fixedPerPage);
            this.showPerPage = false;
        } else {
            this.perPage = splTableDefaults.perPage;  // splTableDefaults is from import
            this.showPerPage = true;
        }
        this.sortByField = this.initialSortField;  // set initial sort field based on prop
        this.sortDesc = this.initialSortDesc;  // set initial sort direction based on prop */
    },
    mounted: function () {
        this.getSetupData();
        this.getGridStateFromLocalStorage();
        this.getList();
    },
    methods: {
        // PUBLIC METHOD YOU CAN CALL FROM PARENT TO REFRESH LIST, likely just as $refs.something.refreshList();
        refreshList: function(noFilterReset = false) {
            if (noFilterReset) {
                this.getNewList();  // if you specifically set noFilterReset to true (first positional arg), then don't reset filters (but do reset current page to 1)
            } else {
                this.clearAllFilters();   // this resets all filter values and reloads the list
            }
        },
        // internal methods
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        resetFilterValues: function() {
            this.currentFilterValues = Object.assign({}, this.initialFilterValues);
            // new way
            const filterRefs = ['searchBox'];
            filterRefs.forEach(refName => {
                if (this.$refs[refName]) {
                    this.$refs[refName].reset();
                }
            });
        },
        setInitialFromDefaults() {
            // we call this from created() hook to set initialFilterValues from defaultFilterValues -- and perPage, sortByField, sortDesc, etc.
            // merge any presetFilters (object from prop) with this.defaultFilterValues to make this.initialFilterValues
            if (this.presetFilters && !isEmpty(this.presetFilters)) {
                // target is empty object so you don't mutate originals, merge presetFilters last
                this.initialFilterValues = Object.assign({}, this.defaultFilterValues, this.presetFilters);  
            } else {
                // if we DO NOT have any presetFilters, we need to just do same thing, but without presetFilters... maybe there's a shorter way, but this works
                this.initialFilterValues = Object.assign({}, this.defaultFilterValues);
            }
            this.resetFilterValues();
            // FIGURE OUT THE perPage to use
            if (this.fixedPerPage) {
                this.perPage = Number(this.fixedPerPage);
                this.showPerPage = false;
            } else {
                this.perPage = splTableDefaults.perPage;  // splTableDefaults is from import
                this.showPerPage = true;
            }
            this.sortByField = this.initialSortField;  // set initial sort field based on prop
            this.sortDesc = this.initialSortDesc;  // set initial sort direction based on prop
        },
        // SET / GET FROM LOCAL STORAGE
        setGridStateInLocalStorage() {
            if (!this.saveState) {
                return;  // if we don't want to save state (from props) just return
            }
            console.log('Starting setGridStateInLocalStorage - currentFilterValues:', this.currentFilterValues);
            const filterKeysToExclude = ['searchFilter'];  // don't save searchFilter in local storage, it doesn't make sense, and it doesn't currently work
            const currentFilters = {};
            Object.keys(this.currentFilterValues).forEach(key => {
                if (!filterKeysToExclude.includes(key)) {
                    currentFilters[key] = this.currentFilterValues[key];
                }
            });
            const gridState = {
                perPage: this.perPage,
                currentFilterValues: currentFilters,
                sortByField: this.sortByField,
                sortDesc: this.sortDesc,
            };
            const key = this.localStorageKey;
            const gridStateString = JSON.stringify(gridState);
            console.log('Setting grid state in local storage', key, gridStateString);
            localStorage.setItem(key, gridStateString);
        },
        getGridStateFromLocalStorage() {
            if (!this.saveState) {
                return;  // if we don't want to save state (from props) just return
            }
            const gridStateRaw = localStorage.getItem(this.localStorageKey);
            if (gridStateRaw) {
                console.log('Found grid state in local storage', gridStateRaw);
                const gridState = JSON.parse(gridStateRaw);
                this.perPage = 'perPage' in gridState ? gridState.perPage : this.perPage;
                // we must 'merge' the currentFilterValues from local storage with the currentFilterValues in data -- because it might not include all filters (like searchFilter)
                this.currentFilterValues = 'currentFilterValues' in gridState ? Object.assign({}, this.currentFilterValues, gridState.currentFilterValues) : this.currentFilterValues;
                this.sortByField = 'sortByField' in gridState ? gridState.sortByField : this.sortByField;
                this.sortDesc = 'sortDesc' in gridState ? gridState.sortDesc : this.sortDesc;
            } else {
                console.log('No grid state found in local storage');
            }
        },
        clearGridStateFromLocalStorage() {
            // allow calling this even if this.saveState is false
            console.log('Clearing grid state from local storage');
            localStorage.removeItem(this.localStorageKey);
        },
        clearSavedStateAndRefresh() {
            // clear saved state from local storage, then refresh list
            this.clearGridStateFromLocalStorage();
            this.setInitialFromDefaults();
            this.refreshList();  // let the filters get reset also
        },
        setFilterAndSearch(filterKey, value) {
            // pass in which filter to set and the value - implemented with search box first... add others later
            console.log('filterKey and value', filterKey, value);
            this.currentFilterValues[filterKey] = value;
            this.getNewList();
        },
        clearAllFilters: function() {
            // clear/reset all filters and search
            this.resetFilterValues();
            this.getNewList();
        },
        makeStateFilterOptions(state_options) {  // NEW STATE MACHINE VERSION
            // add in some 'special' status values -- make sure the viewset on backend knows what to do with them
            // this takes list of state_options (list of full state_dict Objects/Dicts)
            // then adds at least one 'custom' one -- for '__all_active__' -- meaning all active statuses
            // make sure keys match what filter component is looking for for value-field (value) and text-field (title)
            const allActiveOption = { value: '__not_archived__', title: 'Not Archived' };
            const allStatesOption = { value: null, title: 'All' };
            return [ allActiveOption, allStatesOption, ...state_options ];
        },
        getSetupData: function() {
            // get data needed only at beginning, for populating select boxes, etc.
            this.loadingSetupData = true;
            suppliosApi.rfx.getRfxSetupOptions()
            .then(response => {
                // change some things to make local data how you want it
                let modifiedSetupData = cloneDeep(response.data);  // clone it first, then change it if you find things you need to change
                if (response.data.state_options) {
                    const preppedStateOptions = this.makeStateFilterOptions(response.data.state_options);
                    modifiedSetupData['state_options'] = preppedStateOptions;  // overwrite with your modified version 
                }
                this.setupData = Object.assign({}, modifiedSetupData);
            })
            .catch(error => this.handleError(error, 'Error while getting list options'))
            .finally(() => this.loadingSetupData = false)
        },
        getOrderingFieldString: function() {
            // look up what field name (string) we should use to send to API, in case key here is different than what API should order by
            // useful if you have serializer methods that get values from related objects... you can use double-underscore notation in DRF for ordering
            // also add '-' if sortDesc is true
            let apiOrderingName = this.fields.find(fld => fld.key === this.sortByField).api_ordering_name;
            return (this.sortDesc ? '-' : '') + apiOrderingName;
        },
        getNewList: function() {
            // call getList(), but must reset currentPage first
            // call this from any change to filters, sort, search, etc
            this.currentPage = 1;
            this.$nextTick(() => {
                // we wait until nextTick so the reactive things from table are updated (sortByField, sortDesc, etc.)
                this.setGridStateInLocalStorage();
                this.getList();
            });
        },
        getList: function() {  
            this.loading = true;
            // axiosConfig['params'] is where we put search/filter params
            let filterParams = {  // grab the current search/filter values
                search: this.currentFilterValues.searchFilter || null,
                grouping: this.currentFilterValues.grouping, 
                rfx_type: this.currentFilterValues.rfxTypeFilter || null,
                sm_state: this.currentFilterValues.stateFilter || null,
                sourcing_category: this.currentFilterValues.categoryFilter,
                sourcing_tags: this.currentFilterValues.tagsFilter,
                sourcing_owner: this.currentFilterValues.ownerFilter,
                award_status: this.currentFilterValues.awardStatusFilter,
            };
            let orderingParams = {
                ordering: this.getOrderingFieldString(),
            };
            let paginationParams = {
                page: this.currentPage,
                page_size: this.perPage,
            };
            let combinedParams = Object.assign({}, filterParams, orderingParams, paginationParams);
            let axiosConfig = {params: combinedParams};
            suppliosApi.rfx[this.endpointName](axiosConfig)
            .then(response => {
                this.totalRows = response.data.count || 0;
                // this.itemList = response.data.results;
                this.itemList = response.data.results.map(item => new Rfx(item));
                this.$emit('list-loaded', this.totalRows);  // emit total count to parent
                //console.log("Got itemList from getList function");
                //console.log(this.itemList);
                // setting hasAppliedFilters must be done once we get the response, not before
                this.hasAppliedFilters = Object.values(filterParams).some(val => !!val);  // goes through all filter values, if any are not empty/null, returns true
            })
            .catch(error => {
                this.handleError(error, 'Error while loading itemList');
            })
            .finally(() => {
                //console.log('hello from finally of getList');
                this.initialLoading = false;  // set flag to say we already loaded once
                this.loading = false;
            });
        },
        handleAddNewRfx: function() {
            // just emits something to parent so parent can call method on add new event component
            this.$emit('request-create-rfx');
        },
        approachingDeadline: function(value) {
            let approachingWindow = { days: 1 };
            return DateApproachingDeadline(value, approachingWindow);
        },
        isFilterEnabled: function(filterName) {
            // checks if a specific filter is enabled
            return this.showFilters.includes(filterName);
        },
        // formatters -- replacing filters
        formatDate: function(value) {
            return FormatDateHelper(value);
        },
        formatDateTime: function(value) {
            return FormatDateTimeLocal(value);
        },
        humanizeDateTime: function(value) {
            return HumanizeDateTimeHelper(value);
        },
    }
}
</script>

<style>

.rfx-list-base-table table.table th, 
.rfx-list-base-table table.table td {
    padding: 0.5rem;
}

.rfx-list-base-table .table th[aria-sort="ascending"],
.rfx-list-base-table .table th[aria-sort="descending"] {
    color: var(--primary);
}

</style>
